/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { setSearchRoute } from '../../helpers/history';
import l10n from '../../shared/strings';

import { CHANNEL_RECORD_FILTER, LISTING_STATUS } from './constants/listingStatus';
import { fetchAggregatedProductDetails } from './fetchAggregatedProductDetails';
import { pinEditLiveSearch } from './pinEditLiveSearch';
import { search } from './search';
import { setProductListingActiveTab } from './setProductListingActiveTab';
import openDraftProductRecord from './helpers/openDraftProductRecord';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;

const toId = (value) => String(value?.id ?? value);

const collectUnsuccessfulIds = (data) => new Set([
  ...(Array.isArray(data?.skipped) ? data.skipped : []),
  ...(Array.isArray(data?.failed) ? data.failed : []),
].map(toId));

const listingLevelCounts = ({
  productIds,
  masterVariantIds,
  entries = [],
  unsuccessfulIds,
  drafted,
}) => {
  const productIdSet = new Set(productIds.map(String));
  const listingIdSet = new Set(productIdSet);
  const variantMasterIdsByListing = new Map();

  entries.forEach((entry) => {
    if (entry?.type !== 'listingVariant' || !entry.baseProductId) return;
    const listingId = String(entry.baseProductId);
    listingIdSet.add(listingId);
    if (!entry.masterVariantId) return;
    const variantIds = variantMasterIdsByListing.get(listingId) || [];
    variantIds.push(String(entry.masterVariantId));
    variantMasterIdsByListing.set(listingId, variantIds);
  });

  if (!listingIdSet.size) {
    const variantTotal = masterVariantIds.length;
    const variantDrafted = masterVariantIds.filter((id) => !unsuccessfulIds.has(String(id))).length;
    return {
      drafted: variantDrafted || drafted,
      total: variantTotal || drafted,
    };
  }

  let draftedListingCount = 0;
  listingIdSet.forEach((listingId) => {
    if (productIdSet.has(listingId)) {
      if (!unsuccessfulIds.has(listingId)) draftedListingCount += 1;
      return;
    }
    const variantIds = variantMasterIdsByListing.get(listingId) || [];
    if (variantIds.some((variantId) => !unsuccessfulIds.has(variantId))) {
      draftedListingCount += 1;
    }
  });

  return { drafted: draftedListingCount, total: listingIdSet.size };
};

const collectSuccessfulEntries = (entries, unsuccessfulIds) => (
  entries.filter((entry) => {
    if (!entry?._id) return false;
    if (unsuccessfulIds.has(String(entry._id))) return false;
    if (entry.masterVariantId && unsuccessfulIds.has(String(entry.masterVariantId))) {
      return false;
    }
    return true;
  })
);

export const editLiveAttributes = createAsyncThunk(
  'products/editLiveAttributes',
  async ({
    productIds = [],
    masterVariantIds = [],
    channelId,
    entries = [],
  }, {
    dispatch,
    extra: {
      sdk, Toast, Logger,
    },
  }) => {
    try {
      const payload = {};
      if (productIds.length) payload.productIds = productIds;
      if (masterVariantIds.length) {
        payload.masterVariantIds = masterVariantIds;
        payload.salesChannelIds = [channelId];
      }

      const { data } = await sdk.products.createListingDrafts(payload);
      const drafted = Number(data?.drafted) || 0;

      if (!drafted) {
        Toast(strings.draftsPreparedNone, { type: 'error', autoClose: false });
        return { ...data, drafted: 0, didTransition: false };
      }

      const unsuccessfulIds = collectUnsuccessfulIds(data);
      const counts = listingLevelCounts({
        productIds,
        masterVariantIds,
        entries,
        unsuccessfulIds,
        drafted,
      });
      const remaining = Math.max(0, counts.total - counts.drafted);

      if (remaining) {
        Toast(
          `${strings.draftsPreparedSuccess(counts.drafted, counts.total)} ${strings.draftsPreparedForEditingPartial(remaining)}`,
          { type: 'warning', autoClose: false },
        );
      } else {
        Toast(strings.draftsPreparedSuccess(counts.drafted, counts.total || counts.drafted), {
          type: 'success',
        });
      }

      const successfulEntries = collectSuccessfulEntries(entries, unsuccessfulIds)
        .map((entry) => ({ ...entry, listingStatus: LISTING_STATUS.DRAFT }));

      if (successfulEntries.length === 1 && successfulEntries[0].masterProductId) {
        const entry = successfulEntries[0];
        dispatch(setProductListingActiveTab({
          productId: entry.masterProductId,
          channelId,
          tab: LISTING_STATUS.DRAFT,
        }));
        openDraftProductRecord({
          productId: entry.masterProductId,
          variantId: entry.type === 'listingVariant' ? entry.masterVariantId : undefined,
        });
        return { ...data, drafted, didTransition: true };
      }

      if (successfulEntries.length && channelId) {
        dispatch(pinEditLiveSearch({
          ids: successfulEntries.map((entry) => String(entry._id)),
          productLayer: String(channelId),
          listingStatus: CHANNEL_RECORD_FILTER.HAS_DRAFT,
          selectionEntries: successfulEntries,
        }));
        setSearchRoute({
          productLayer: channelId,
          listingStatus: CHANNEL_RECORD_FILTER.HAS_DRAFT,
        }, { isProducts: true });
        await dispatch(search({}));
        await dispatch(fetchAggregatedProductDetails());
        return { ...data, drafted, didTransition: true };
      }

      await dispatch(fetchAggregatedProductDetails());
      return { ...data, drafted, didTransition: false };
    } catch (error) {
      Toast(strings.draftsPreparedNone, { type: 'error', autoClose: false });
      Logger.error(new Error('Bulk edit live attributes failed'), { error }, [
        'EditLiveAttributesFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(editLiveAttributes.rejected, (state, { error }) => {
      state.error = error?.message || 'Cannot prepare drafts from published records';
    });
};
